import React from 'react';
import { Check, TrendingDown, Calendar } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';

const PricingSection = () => {
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const pricingPlans = [
    {
      name: 'Standard',
      period: '4 Weeks',
      weeklyRate: '£36.90',
      totalCost: '£147.60',
      discount: 'Base Rate',
      features: [
        'Pay every 4 weeks in advance',
        'Heavy-duty padlock included',
        'Lock box security',
        'Daily access 7am-7pm',
        '14 days notice to terminate',
        'Flexible rental period',
      ],
      popular: false,
    },
    {
      name: '12 Weeks Prepaid',
      period: '12 Weeks',
      weeklyRate: '£33.21',
      totalCost: '£398.52',
      discount: '10% Discount',
      features: [
        'Save 10% with prepayment',
        '£3.69 per week savings',
        'Heavy-duty padlock included',
        'Lock box security',
        'Daily access 7am-7pm',
        'All standard features',
      ],
      popular: true,
    },
    {
      name: '26 Weeks Prepaid',
      period: '26 Weeks',
      weeklyRate: '£31.37',
      totalCost: '£815.49',
      discount: '15% Discount',
      features: [
        'Save 15% with prepayment',
        '£5.53 per week savings',
        'Heavy-duty padlock included',
        'Lock box security',
        'Daily access 7am-7pm',
        'All standard features',
      ],
      popular: false,
    },
    {
      name: '52 Weeks Prepaid',
      period: '52 Weeks (1 Year)',
      weeklyRate: '£29.98',
      totalCost: '£1,559.03',
      discount: '18.75% Discount',
      features: [
        'Best value - 18.75% discount',
        '£6.92 per week savings',
        'Heavy-duty padlock included',
        'Lock box security',
        'Daily access 7am-7pm',
        'All standard features',
      ],
      popular: false,
      bestValue: true,
    },
  ];

  return (
    <section id="pricing" className="py-16 md:py-24 bg-gradient-to-br from-gray-50 to-red-50/20">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-12">
          <span className="inline-flex items-center px-4 py-2 rounded-full bg-red-100 text-red-700 text-sm font-medium mb-4">
            <TrendingDown className="h-4 w-4 mr-2" />
            Competitive Pricing
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Flexible Pricing Plans
          </h2>
          <p className="text-lg text-gray-600">
            Choose the plan that works best for you. Save more with longer prepaid bookings!
          </p>
        </div>

        {/* Pricing Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {pricingPlans.map((plan, index) => (
            <Card
              key={index}
              className={`relative border-2 transition-all duration-300 ${
                plan.popular
                  ? 'border-red-600 shadow-xl scale-105'
                  : plan.bestValue
                  ? 'border-green-600 shadow-xl'
                  : 'border-gray-200 hover:border-red-200 hover:shadow-lg'
              }`}
            >
              {plan.popular && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-red-600 text-white px-4 py-1 rounded-full text-sm font-semibold">
                  Most Popular
                </div>
              )}
              {plan.bestValue && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-green-600 text-white px-4 py-1 rounded-full text-sm font-semibold">
                  Best Value
                </div>
              )}
              <CardHeader className="text-center pb-4">
                <CardTitle className="text-xl font-bold text-gray-900 mb-2">{plan.name}</CardTitle>
                <p className="text-sm text-gray-600 mb-4">{plan.period}</p>
                <div className="space-y-1">
                  <div className="text-4xl font-bold text-red-600">{plan.weeklyRate}</div>
                  <p className="text-sm text-gray-600">per week</p>
                </div>
                <div className="mt-3 py-2 px-3 bg-green-100 text-green-700 rounded-lg text-sm font-semibold">
                  {plan.discount}
                </div>
              </CardHeader>
              <CardContent>
                <div className="mb-4 p-3 bg-gray-50 rounded-lg text-center">
                  <p className="text-sm text-gray-600">Total Cost</p>
                  <p className="text-2xl font-bold text-gray-900">{plan.totalCost}</p>
                </div>
                <ul className="space-y-3 mb-6">
                  {plan.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start text-sm">
                      <Check className="h-5 w-5 text-green-600 mr-2 flex-shrink-0" />
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
                <Button
                  onClick={() => scrollToSection('contact')}
                  className={`w-full ${
                    plan.popular || plan.bestValue
                      ? 'bg-red-600 hover:bg-red-700 text-white'
                      : 'bg-white hover:bg-gray-50 text-gray-900 border-2 border-gray-300'
                  }`}
                >
                  Book Now
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Additional Info */}
        <div className="grid md:grid-cols-3 gap-6">
          <Card className="border-2 border-gray-200">
            <CardContent className="p-6">
              <Calendar className="h-8 w-8 text-red-600 mb-3" />
              <h3 className="font-semibold text-gray-900 mb-2">Refundable Deposit</h3>
              <p className="text-sm text-gray-600">
                £150 deposit required, fully refundable when container is returned clean with padlock and key.
              </p>
            </CardContent>
          </Card>
          <Card className="border-2 border-gray-200">
            <CardContent className="p-6">
              <Check className="h-8 w-8 text-red-600 mb-3" />
              <h3 className="font-semibold text-gray-900 mb-2">Minimum Notice</h3>
              <p className="text-sm text-gray-600">
                14 days written notice required to terminate, ending on a payment due date.
              </p>
            </CardContent>
          </Card>
          <Card className="border-2 border-gray-200">
            <CardContent className="p-6">
              <TrendingDown className="h-8 w-8 text-red-600 mb-3" />
              <h3 className="font-semibold text-gray-900 mb-2">VAT Included</h3>
              <p className="text-sm text-gray-600">
                All prices include VAT. Annual review on 1 April, fees may increase in line with CPI.
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Important Notice */}
        <div className="mt-8 bg-amber-50 border-l-4 border-amber-500 p-6 rounded-lg">
          <h3 className="font-semibold text-amber-900 mb-2 flex items-center">
            <span className="w-6 h-6 bg-amber-500 rounded-full flex items-center justify-center text-white text-sm mr-2">!</span>
            Insurance Notice
          </h3>
          <p className="text-amber-800 text-sm">
            We do not insure your goods. Storage is at your sole risk and you must have adequate insurance 
            for full replacement value. See our Terms & Conditions for details.
          </p>
        </div>
      </div>
    </section>
  );
};

export default PricingSection;
